/**
 * The document's one observer of `history`, and the location authority over it (kernel apps,
 * WS-14 section 3.6; layered shell surfaces, R-9, R-26, D25). Both move unchanged from the shell's
 * `boot/navigation.ts` and `services/location-service.ts`.
 *
 * The URL is the single location authority. A push inside any surface (a `BrowserRouter`, a
 * surface's `services.location.navigate`, a `replaceState` of the host), the host's own redirects
 * and the back button all reach the watcher, and through it the host's mount choice and each
 * mount's location view. Nothing else in the document patches `history` or listens to `popstate`.
 * The authority subscribes to the watcher, calls no `history` method and adds no `popstate`
 * listener itself, so each change reaches a subscriber once.
 *
 * `isNavigablePath` and `isUnderMount` come from `@zion/shell-chrome/model`, so the chrome, the
 * shell and this host agree on what a path is.
 */
import type { SurfaceLocation, SurfaceLocationService } from '@zion/sdk-surfaces/v1';
import { isNavigablePath, isUnderMount } from '@zion/shell-chrome/model';
import { SurfaceServiceRefusedError, createListenerSet, type MountScope } from './kit.js';

/** One URL change: where the document is now, and what changed it. */
export interface NavigationEvent {
  /** `location.pathname` after the change. */
  readonly pathname: string;
  /** `location.search` after the change, `?` included, or `''`. */
  readonly search: string;
  /** `push` for `pushState`, `replace` for `replaceState`, `pop` for a `popstate` (back, forward). */
  readonly cause: 'push' | 'replace' | 'pop';
}

/**
 * The watcher a host creates once per document and hands to the location authority.
 *
 * - `subscribe` adds a listener called after every URL change, in subscription order, and returns
 *   an idempotent unsubscribe. A listener that throws is reported to `console.error`; the rest run.
 * - `navigate(to, replace)` changes the URL through `history.pushState`, or `replaceState` when
 *   `replace` is true, so listeners hear it once. `to` must pass `isNavigablePath` (one
 *   leading `/`, no `//`, no backslash, no control character, no scheme); any other value
 *   navigates to `/`.
 * - `dispose` restores the window's original `pushState` and `replaceState`, removes the
 *   `popstate` listener and drops every listener. Idempotent. A later `navigate` still changes the
 *   URL and notifies nobody.
 */
export interface NavigationWatcher {
  subscribe(listener: (event: NavigationEvent) => void): () => void;
  navigate(to: string, replace: boolean): void;
  dispose(): void;
}

/** Thrown by {@link createNavigationWatcher} when the window already has a live watcher. */
export class NavigationWatcherInstalledError extends Error {
  override readonly name = 'NavigationWatcherInstalledError';

  constructor() {
    super('a navigation watcher is already installed on this window; dispose it first');
  }
}

/** Windows that hold a live watcher. */
const installed = new WeakSet<Window>();

/**
 * Install the watcher on `win`.
 *
 * Wraps `win.history.pushState` and `win.history.replaceState` so each call runs the original
 * method and then notifies listeners with the new `pathname` and `search`; a call the original
 * method throws on (a cross-origin URL) notifies nobody and rethrows. Listens to `popstate` and
 * notifies with cause `pop`.
 *
 * Throws {@link NavigationWatcherInstalledError} when `win` already has a watcher that has not
 * been disposed, and changes nothing. Side effects: replaces two `history` methods and adds one
 * window listener until `dispose`.
 */
export function createNavigationWatcher(win: Window): NavigationWatcher {
  if (installed.has(win)) throw new NavigationWatcherInstalledError();
  installed.add(win);

  const history = win.history;
  const originalPush = history.pushState;
  const originalReplace = history.replaceState;
  const listeners = new Set<(event: NavigationEvent) => void>();
  let disposed = false;

  const notify = (cause: NavigationEvent['cause']): void => {
    const event: NavigationEvent = Object.freeze({
      pathname: win.location.pathname,
      search: win.location.search,
      cause,
    });
    for (const listener of Array.from(listeners)) {
      try {
        listener(event);
      } catch (err) {
        console.error('[shell] a navigation listener threw', err);
      }
    }
  };

  const wrappedPush: History['pushState'] = function pushState(data, unused, url) {
    originalPush.call(history, data, unused, url);
    notify('push');
  };
  const wrappedReplace: History['replaceState'] = function replaceState(data, unused, url) {
    originalReplace.call(history, data, unused, url);
    notify('replace');
  };
  const onPop = (): void => notify('pop');

  history.pushState = wrappedPush;
  history.replaceState = wrappedReplace;
  win.addEventListener('popstate', onPop);

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    navigate(to, replace) {
      const target = isNavigablePath(to) ? to : '/';
      if (replace) history.replaceState(history.state, '', target);
      else history.pushState(null, '', target);
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      history.pushState = originalPush;
      history.replaceState = originalReplace;
      win.removeEventListener('popstate', onPop);
      listeners.clear();
      installed.delete(win);
    },
  };
}

/**
 * The two members of the {@link NavigationWatcher} the authority uses, taken from its declaration
 * above, so a change to the watcher's signature reaches the authority. The
 * authority's listener ignores the `NavigationEvent` it receives and reads the location itself;
 * `navigate(to, replace)` changes the URL and then notifies.
 */
export type HistorySource = Pick<NavigationWatcher, 'subscribe' | 'navigate'>;

/** The three URL parts the authority reads; the live `window.location` in production. */
export interface DocumentLocation {
  readonly pathname: string;
  readonly search: string;
  readonly hash: string;
}

/**
 * The document-wide location owner.
 *
 * - `current()` reads the three parts from the location it was given, as a new frozen object.
 * - `navigate(to, replace)` throws `path-invalid` unless `isNavigablePath` of `@zion/shell-chrome/model` admits `to`, so
 *   the watcher's fallback to `/` never fires for a surface. A `to` equal to the current pathname,
 *   search and hash does nothing. Any other calls `history.navigate(to, replace === true)`.
 * - `subscribe` hears each watcher notification once.
 * - `view(scope)` is the mount's {@link SurfaceLocationService}. A routed view's `current()` adds
 *   `mountPath` and `subpath` ({@link stripMount}); an embedded or system view reports `null` for
 *   both. An embedded view's `navigate` throws `embedded-navigation` before it checks the path
 *   (D5, D14). The view's `current()` keeps its identity while the URL is unchanged, and its
 *   subscriptions end with the mount.
 * - `dispose()` removes its one watcher subscription and every subscriber; the watcher's other
 *   subscribers stay.
 */
export interface LocationAuthority {
  current(): DocumentLocation;
  navigate(to: string, replace?: boolean): void;
  subscribe(listener: () => void): () => void;
  view(scope: MountScope): SurfaceLocationService;
  dispose(): void;
}

/**
 * The path under a routed mount, with `isUnderMount` semantics (case-insensitive, a trailing
 * slash on the mount ignored).
 *
 * @returns The remainder after the mount prefix in the pathname's own case, `/` for the mount
 *   itself, the whole pathname for a root mount `/`, and `null` for a path outside the mount or
 *   an empty `mountPath`. Pure.
 */
export function stripMount(pathname: string, mountPath: string): string | null {
  if (!isUnderMount(pathname, mountPath)) return null;
  const base = mountPath.length > 1 && mountPath.endsWith('/') ? mountPath.slice(0, -1) : mountPath;
  if (base === '/') return pathname;
  const rest = pathname.slice(base.length);
  return rest === '' ? '/' : rest;
}

/** Build the authority over the watcher and the location it reads. */
export function createLocationAuthority(history: HistorySource, location: DocumentLocation): LocationAuthority {
  const listeners = createListenerSet();
  const stopWatching = history.subscribe(() => listeners.notify());

  const current = (): DocumentLocation =>
    Object.freeze({ pathname: location.pathname, search: location.search, hash: location.hash });

  const navigate = (to: string, replace?: boolean): void => {
    if (!isNavigablePath(to)) {
      throw new SurfaceServiceRefusedError('path-invalid', 'location.navigate takes an absolute same-origin path');
    }
    if (to === `${location.pathname}${location.search}${location.hash}`) return;
    history.navigate(to, replace === true);
  };

  return {
    current,
    navigate,
    subscribe: (listener) => listeners.add(listener),
    view(scope) {
      const placement = scope.request.placement;
      let cached: SurfaceLocation | null = null;
      const viewCurrent = (): SurfaceLocation => {
        const { pathname, search, hash } = location;
        if (cached !== null && cached.pathname === pathname && cached.search === search && cached.hash === hash) {
          return cached;
        }
        const mountPath = placement.kind === 'routed' ? placement.mountPath : null;
        cached = Object.freeze({
          pathname,
          search,
          hash,
          mountPath,
          subpath: mountPath === null ? null : stripMount(pathname, mountPath),
        });
        return cached;
      };
      return scope.guard({
        current: viewCurrent,
        navigate: (to: string, replace?: boolean) => {
          if (placement.kind === 'embedded') {
            throw new SurfaceServiceRefusedError('embedded-navigation', 'an embedded surface emits navigate instead');
          }
          navigate(to, replace);
        },
        subscribe: (listener: () => void) => {
          const remove = listeners.add(listener);
          scope.own(remove);
          return remove;
        },
      });
    },
    dispose() {
      stopWatching();
      listeners.clear();
    },
  };
}
